import '@fontsource-variable/shantell-sans/full.css';
import '@fontsource/vt323';
import './styles.css';

import { registerSW } from 'virtual:pwa-register';
import { reverbShape, grainPitch, grainSize } from './audio/effects';
import { AudioEngine, type EffectId } from './audio/engine';
import { PRESETS, presetIndex } from './audio/presets';
import { attachKeyboard, MAX_OCTAVE, MIN_OCTAVE, noteName, type Command } from './input/keyboard';
import { attachMidi, type MidiStatus } from './input/midi';
import { fileStamp, slug, type StereoAudio } from './record/audio-data';
import { Capture, MAX_SECONDS, type CaptureState } from './record/capture';
import { download, mp3Blob, wavBlob } from './record/export';
import { loadTakes, removeTake, saveTake, type Take } from './record/takes-store';
import { VideoCapture } from './record/video';
import { loadSettings, saveSettings, storeGet, storeSet, type Mode } from './settings';
import { Knob } from './ui/knob';
import { bindWheel, KeysView } from './ui/keys-view';
import { ink, newSeed, roundedRect, sketch, sketchAll } from './ui/sketch';
import { TakesView } from './ui/takes-view';
import { toast } from './ui/toast';
import { NOTE_COLORS, Visualizer, type VisSettings } from './ui/visualizer';

const $ = <T extends HTMLElement = HTMLElement>(sel: string) => document.querySelector(sel) as T;

const MAX_TAKES = 8;
const app = $('#app');
const settings = loadSettings();
const engine = new AudioEngine();
let presetIdx = presetIndex(settings.presetId);

// ------------------------------------------------------------------ views

type View = 'landing' | Mode;

const view = (): View => app.dataset.view as View;

function go(next: View, focus = true): void {
  app.dataset.view = next;
  const hash = next === 'landing' ? '' : `#${next}`;
  if (location.hash !== hash) history.replaceState(null, '', hash || location.pathname);
  document.querySelectorAll<HTMLButtonElement>('.viewswitch [data-go]').forEach((b) => {
    b.setAttribute('aria-current', String(b.dataset.go === next));
  });
  if (next !== 'landing') {
    settings.lastMode = next;
    saveSettings(settings);
  }
  if (next === 'visual') visualizer.start();
  else if (!videoCapture.recording) visualizer.stop();
  if (next === 'landing' && focus) {
    document.querySelector<HTMLElement>(`.choice[data-go="${settings.lastMode}"]`)?.focus({ preventScroll: true });
  }
}

document.addEventListener('click', (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-go]');
  if (!el) return;
  e.preventDefault();
  engine.resume();
  go(el.dataset.go as View);
});

// A mouse click shouldn't leave a button focused: Space is the play shortcut,
// and would otherwise re-press whatever was clicked last.
document.addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest('button');
  if (b && e.detail > 0 && !b.closest('dialog')) b.blur();
});

// Browsers keep audio suspended until the first click, tap or key press.
const unlock = () => engine.resume();
window.addEventListener('pointerdown', unlock, { capture: true });
window.addEventListener('keydown', unlock, { capture: true });

// ------------------------------------------------------------------ screen

const screen = $('#screen');
let statusTimer = 0;

function renderPreset(): void {
  const p = PRESETS[presetIdx];
  $('#preset-num').textContent = String(presetIdx + 1).padStart(2, '0');
  $('#preset-name').textContent = p.name;
  $('#preset-blurb').textContent = p.blurb;
  screen.classList.remove('is-flash');
  void screen.offsetWidth;
  screen.classList.add('is-flash');
}

function renderStatus(): void {
  const midi = midiNames.length ? ' · midi' : '';
  $('#screen-status').textContent = `oct ${settings.octave} · vel ${settings.velocity}${midi}`;
}

/** Briefly show a message on the screen's bottom line. */
function flashStatus(text: string, ms = 1400): void {
  clearTimeout(statusTimer);
  $('#screen-status').textContent = text;
  statusTimer = window.setTimeout(renderStatus, ms);
}

let pianoReady = false;

function setPreset(i: number): void {
  presetIdx = (i + PRESETS.length) % PRESETS.length;
  const p = PRESETS[presetIdx];
  settings.presetId = p.id;
  saveSettings(settings);
  releaseAllNotes();
  const ok = engine.setPreset(p);
  renderPreset();
  if (!ok && !pianoReady) flashStatus('loading piano…', 4000);
}

$('#preset-prev').addEventListener('click', () => setPreset(presetIdx - 1));
$('#preset-next').addEventListener('click', () => setPreset(presetIdx + 1));

function setOctave(o: number): void {
  const next = Math.max(MIN_OCTAVE, Math.min(MAX_OCTAVE, o));
  if (next === settings.octave) {
    flashStatus(next === MIN_OCTAVE ? 'lowest octave' : 'highest octave');
    return;
  }
  settings.octave = next;
  $('#oct-value').textContent = String(next);
  saveSettings(settings);
  renderStatus();
}
$('#oct-down').addEventListener('click', () => setOctave(settings.octave - 1));
$('#oct-up').addEventListener('click', () => setOctave(settings.octave + 1));

function setVelocity(v: number): void {
  settings.velocity = Math.max(20, Math.min(127, v));
  saveSettings(settings);
  renderStatus();
}

// ------------------------------------------------------------------- notes

const sounding = new Set<number>();

const keyColor = (note: number): string | undefined =>
  view() === 'visual' && settings.vis.palette === 'notes' ? NOTE_COLORS[note % 12] : undefined;

function noteOn(note: number, velocity: number): void {
  engine.resume();
  if (view() === 'landing') go(settings.lastMode);
  if (!engine.hasInstrument) {
    flashStatus(pianoReady ? 'one moment…' : 'loading piano…', 1500);
    return;
  }
  if (sounding.has(note)) noteOff(note);
  sounding.add(note);
  engine.noteOn(note, velocity);
  keys.press(note, keyColor(note));
  visualizer.noteOn(note, velocity);
  if (!app.classList.contains('has-played')) {
    app.classList.add('has-played');
    storeSet('played', '1');
  }
}

function noteOff(note: number): void {
  if (!sounding.delete(note)) return;
  engine.noteOff(note);
  keys.release(note);
  visualizer.noteOff(note);
}

function releaseAllNotes(): void {
  releaseKeyboard();
  for (const n of [...sounding]) noteOff(n);
  engine.allNotesOff();
  keys.clear();
  visualizer.clearNotes();
}

const keys = new KeysView($('#keys'), {
  down: (pc) => {
    const note = (settings.octave + 1) * 12 + pc;
    noteOn(note, settings.velocity);
    return note;
  },
  up: (note) => noteOff(note),
});

const bendWheel = bindWheel($('#wheel-bend'), { spring: true, onChange: (v) => engine.setBend(v * 2) });
const modWheel = bindWheel($('#wheel-mod'), { spring: false, onChange: (v) => engine.setMod(v) });

const releaseKeyboard = attachKeyboard({
  noteOn: (n) => noteOn(n, settings.velocity),
  noteOff,
  octave: () => settings.octave,
  command: (c: Command) => {
    switch (c) {
      case 'octave-down': return setOctave(settings.octave - 1);
      case 'octave-up': return setOctave(settings.octave + 1);
      case 'velocity-down': return setVelocity(settings.velocity - 20);
      case 'velocity-up': return setVelocity(settings.velocity + 20);
      case 'prev-preset': return setPreset(presetIdx - 1);
      case 'next-preset': return setPreset(presetIdx + 1);
      case 'record': return toggleRecord();
      case 'play-take': return playLatest();
      case 'help': return openHelp();
    }
  },
});

// --------------------------------------------------------------------- MIDI

let midiNames: string[] = [];

function renderMidi(s: MidiStatus): void {
  const el = $('#midi-status');
  el.dataset.kind = s.kind;
  const text = el.querySelector('.midi__text')!;
  const prev = midiNames;
  midiNames = s.kind === 'connected' ? s.names : [];
  if (s.kind === 'connected') {
    text.textContent = s.names.length > 1 ? `${s.names[0]} +${s.names.length - 1}` : s.names[0];
    el.title = `MIDI connected: ${s.names.join(', ')}`;
    const fresh = s.names.find((n) => !prev.includes(n));
    if (fresh) toast(`${fresh} connected. Play away.`);
  } else {
    text.textContent = { unsupported: 'no MIDI in this browser', denied: 'MIDI blocked', waiting: 'no MIDI keyboard' }[s.kind];
    el.title =
      s.kind === 'unsupported'
        ? 'This browser has no Web MIDI. Use Chrome, Edge or Firefox for MIDI keyboards. Your computer keyboard works everywhere.'
        : s.kind === 'denied'
          ? 'MIDI access was blocked. Allow it in the site settings to use a MIDI keyboard.'
          : 'Plug in a USB MIDI keyboard and it connects automatically.';
  }
  renderStatus();
}

void attachMidi({
  noteOn: (n, v) => noteOn(n, v),
  noteOff,
  sustain: (on) => engine.setSustain(on),
  bend: (v) => {
    engine.setBend(v * 2);
    bendWheel.set(v);
  },
  mod: (v) => {
    engine.setMod(v);
    modWheel.set(v);
  },
  allOff: releaseAllNotes,
  status: renderMidi,
});

// ------------------------------------------------------------------ effects

const knobsFor: Partial<Record<EffectId, Knob[]>> = {};

function buildEffects(): void {
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  const defs: Record<EffectId, { a: [string, (v: number) => string, number?]; b: [string, (v: number) => string, number?] }> = {
    reverb: {
      a: ['size', (v) => `${reverbShape(v, 0.5).decay.toFixed(1)} s`],
      b: ['tone', pct],
    },
    saturation: {
      a: ['drive', pct],
      b: ['tone', pct],
    },
    granular: {
      a: ['grain', (v) => `${Math.round(grainSize(v) * 1000)} ms`],
      b: ['pitch', (v) => { const p = grainPitch(v); return p === 0 ? '0 st' : `${p > 0 ? '+' : '−'}${Math.abs(p)} st`; }, 5],
    },
  };
  for (const id of Object.keys(defs) as EffectId[]) {
    const host = document.querySelector<HTMLElement>(`.fx[data-fx="${id}"]`)!;
    const box = host.querySelector<HTMLElement>('.fx__knobs')!;
    const s = settings.fx[id];
    const update = () => {
      engine.setEffect(id, s);
      host.classList.toggle('is-on', s.mix > 0.005);
      saveSettings(settings);
    };
    const mix = new Knob({
      label: 'mix', size: 'lg', value: s.mix, defaultValue: 0, format: pct,
      onChange: (v) => { s.mix = v; update(); },
    });
    const [aLabel, aFmt, aSteps] = defs[id].a;
    const [bLabel, bFmt, bSteps] = defs[id].b;
    const a = new Knob({
      label: aLabel, size: 'sm', value: s.a, defaultValue: s.a, format: aFmt, steps: aSteps,
      onChange: (v) => { s.a = v; update(); },
    });
    const b = new Knob({
      label: bLabel, size: 'sm', value: s.b, defaultValue: s.b, format: bFmt, steps: bSteps,
      onChange: (v) => { s.b = v; update(); },
    });
    mix.el.setAttribute('aria-label', `${id} mix`);
    a.el.setAttribute('aria-label', `${id} ${aLabel}`);
    b.el.setAttribute('aria-label', `${id} ${bLabel}`);
    const small = document.createElement('div');
    small.className = 'fx__small';
    small.append(a.el, b.el);
    box.append(mix.el, small);
    knobsFor[id] = [mix, a, b];
    engine.setEffect(id, s);
    host.classList.toggle('is-on', s.mix > 0.005);
  }
  const vol = new Knob({
    label: 'volume', size: 'lg', value: settings.volume, defaultValue: 0.8, format: pct,
    onChange: (v) => { settings.volume = v; engine.setVolume(v); saveSettings(settings); },
  });
  $('#master-knobs').append(vol.el);
  engine.setVolume(settings.volume);
}

// --------------------------------------------------------------- visualizer

const visCanvas = $<HTMLCanvasElement>('#vis-canvas');
const visualizer = new Visualizer(
  visCanvas,
  engine.analyser,
  () => ({
    reverb: settings.fx.reverb.mix,
    saturation: settings.fx.saturation.mix,
    drive: settings.fx.saturation.a,
    granular: settings.fx.granular.mix,
  }),
  settings.vis,
);

function renderVisControls(): void {
  document.querySelectorAll<HTMLElement>('.visual .seg[data-setting]').forEach((seg) => {
    const key = seg.dataset.setting as keyof VisSettings;
    seg.querySelectorAll<HTMLButtonElement>('button').forEach((b) => {
      const on = String(settings.vis[key]) === b.dataset.value;
      b.setAttribute('aria-checked', String(on));
      b.tabIndex = on ? 0 : -1;
    });
  });
  $('#studio').dataset.aspect = settings.vis.aspect;
}

function setVis(patch: Partial<VisSettings>): void {
  Object.assign(settings.vis, patch);
  visualizer.update(patch);
  saveSettings(settings);
  renderVisControls();
}

document.querySelectorAll<HTMLElement>('.visual .seg[data-setting]').forEach((seg) => {
  const key = seg.dataset.setting as keyof VisSettings;
  seg.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-value]');
    if (b) setVis({ [key]: b.dataset.value } as Partial<VisSettings>);
  });
  // Radio-group arrow keys.
  seg.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    e.stopPropagation();
    const btns = [...seg.querySelectorAll<HTMLButtonElement>('button')];
    const i = btns.findIndex((b) => b.getAttribute('aria-checked') === 'true');
    const next = btns[(i + (e.key === 'ArrowRight' ? 1 : -1) + btns.length) % btns.length];
    setVis({ [key]: next.dataset.value } as Partial<VisSettings>);
    next.focus();
  });
});

$<HTMLInputElement>('#vis-caption').value = settings.vis.caption;
$<HTMLInputElement>('#vis-caption').addEventListener('input', (e) => setVis({ caption: (e.target as HTMLInputElement).value }));
// Enter or Escape hands the keyboard back to the synth.
$<HTMLInputElement>('#vis-caption').addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur();
});
$<HTMLInputElement>('#vis-tag').checked = settings.vis.tag;
$<HTMLInputElement>('#vis-tag').addEventListener('change', (e) => setVis({ tag: (e.target as HTMLInputElement).checked }));

async function useImage(source: Blob, persist: boolean): Promise<void> {
  const bmp = await createImageBitmap(source);
  // Scale big photos down and remember them (as PNG if they have transparency).
  const max = 1600;
  const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * scale);
  c.height = Math.round(bmp.height * scale);
  const g = c.getContext('2d')!;
  g.drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close();
  const px = g.getImageData(0, 0, c.width, c.height).data;
  let transparent = false;
  for (let i = 3; i < px.length; i += 16) {
    if (px[i] < 240) {
      transparent = true;
      break;
    }
  }
  visualizer.setImage(await createImageBitmap(c));
  if (persist) {
    // Logos (with transparency) sit centered; photos fill the frame.
    setVis({ imageFit: transparent ? 'fit' : 'fill' });
    storeSet('image', null);
    storeSet('image', c.toDataURL(transparent ? 'image/png' : 'image/jpeg', 0.86));
  }
  $('#vis-image-label').textContent = 'change image';
  $('#vis-image-clear').hidden = false;
  $('#vis-fit').hidden = false;
}

$<HTMLInputElement>('#vis-image').addEventListener('change', async (e) => {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  try {
    await useImage(file, true);
  } catch {
    toast('That image could not be opened. Try a PNG or JPG.');
  }
});

$('#vis-image-clear').addEventListener('click', () => {
  visualizer.setImage(null);
  storeSet('image', null);
  $('#vis-image-label').textContent = 'add your image';
  $('#vis-image-clear').hidden = true;
  $('#vis-fit').hidden = true;
});

const savedImage = storeGet('image');
if (savedImage) {
  void fetch(savedImage)
    .then((r) => r.blob())
    .then((b) => useImage(b, false))
    .catch(() => storeSet('image', null));
}

// ------------------------------------------------------------------- scope

function runScope(): void {
  const c = $<HTMLCanvasElement>('#scope');
  const g = c.getContext('2d')!;
  const data = new Float32Array(engine.analyser.fftSize);
  const draw = () => {
    requestAnimationFrame(draw);
    if (document.hidden || view() === 'landing') return;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const w = c.clientWidth;
    const h = c.clientHeight;
    if (!w) return;
    if (c.width !== Math.round(w * dpr)) {
      c.width = Math.round(w * dpr);
      c.height = Math.round(h * dpr);
    }
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    engine.analyser.getFloatTimeDomainData(data);
    let start = 0;
    for (let i = 1; i < data.length / 2; i++) {
      if (data[i - 1] < 0 && data[i] >= 0) {
        start = i;
        break;
      }
    }
    g.strokeStyle = getComputedStyle(screen).color;
    g.lineWidth = 1.5;
    g.beginPath();
    const span = 700;
    for (let i = 0; i <= 80; i++) {
      const v = data[start + Math.floor((i / 80) * span)] ?? 0;
      const x = (i / 80) * w;
      const y = h / 2 - Math.max(-1, Math.min(1, v * 2.2)) * (h / 2 - 2);
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke();
  };
  draw();
}

// ----------------------------------------------------------------- recording

const recBtn = $('#rec-btn');
const recStatus = $('#rec-status');
const videoCapture = Object.assign(new VideoCapture(), { recording: false });
let lastTakeNumber = settings.takeCounter;

const takes: Take[] = [];
const takesView = new TakesView($<HTMLOListElement>('#takes'), $('#takes-empty'), {
  toggle: (t, at) => togglePlay(t, at),
  exportWav: (t) => download(wavBlob(t.audio), `${fileBase(t)}.wav`),
  exportMp3: async (t, btn) => {
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = '…';
    try {
      download(await mp3Blob(t.audio), `${fileBase(t)}.mp3`);
    } catch {
      toast('MP3 export failed. WAV always works.');
    } finally {
      btn.disabled = false;
      btn.textContent = label;
    }
  },
  exportVideo: (t) => t.video && download(t.video.blob, `${fileBase(t)}.${t.video.ext}`),
  remove: (t) => deleteTake(t),
  dragFile: (t) => ({ url: wavUrl(t), filename: `${fileBase(t)}.wav` }),
});

const fileBase = (t: Take) => `spiel-${slug(t.name)}-${slug(t.presetName)}-${fileStamp(new Date(t.createdAt))}`;

const wavUrls = new Map<string, string>();
function wavUrl(t: Take): string {
  let url = wavUrls.get(t.id);
  if (!url) {
    url = URL.createObjectURL(wavBlob(t.audio));
    wavUrls.set(t.id, url);
  }
  return url;
}

const capture = new Capture(engine, {
  onState: (s: CaptureState) => {
    recBtn.setAttribute('aria-pressed', String(s !== 'idle'));
    recBtn.dataset.state = s;
    app.dataset.rec = s;
    recStatus.innerHTML =
      s === 'recording'
        ? 'recording · press <kbd>R</kbd> to stop'
        : s === 'tail'
          ? 'letting the last notes ring out…'
          : 'press <kbd>R</kbd> to record up to a minute';
    if (s === 'idle') {
      $('#rec-time').textContent = '0:00';
      $('#rec-progress').style.transform = 'scaleX(0)';
    }
  },
  onTick: (t) => {
    $('#rec-time').textContent = `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
    $('#rec-progress').style.transform = `scaleX(${t / MAX_SECONDS})`;
  },
  onLimit: () => toast('That’s a minute. Saving your take.'),
  tailSeconds: () => {
    const r = settings.fx.reverb;
    const reverbTail = r.mix > 0.01 ? reverbShape(r.a, r.b).decay * (0.4 + 0.6 * r.mix) : 0;
    const p = PRESETS[presetIdx];
    const release = p.kind === 'sampler' ? p.release : p.amp.r;
    return Math.max(reverbTail, release * 1.5);
  },
  onDone: async (audio) => {
    finalizing = true;
    try {
      await saveRecording(audio);
    } finally {
      finalizing = false;
    }
  },
});

/** True between the audio finishing and the take (and its video) being saved. */
let finalizing = false;

async function saveRecording(audio: StereoAudio | null): Promise<void> {
  const video = videoCapture.recording ? await videoCapture.stop() : null;
  videoCapture.recording = false;
  $('#vis-badge').hidden = true;
  if (view() !== 'visual') visualizer.stop();
  if (!audio) {
    toast('Nothing to save. Play something while the red light is on.');
    return;
  }
  settings.takeCounter = ++lastTakeNumber;
  saveSettings(settings);
  const take: Take = {
    id: crypto.randomUUID(),
    name: `Take ${lastTakeNumber}`,
    presetName: PRESETS[presetIdx].name,
    createdAt: Date.now(),
    audio,
    video: video ?? undefined,
  };
  takes.unshift(take);
  takesView.add(take);
  void saveTake(take);
  enforceTakeLimit();
}

function enforceTakeLimit(): void {
  let evicted = false;
  while (takes.length > MAX_TAKES) {
    const old = takes.pop()!;
    if (playback?.id === old.id) stopPlayback();
    takesView.remove(old.id);
    forgetTake(old);
    evicted = true;
  }
  if (evicted) toast(`Spiel keeps your ${MAX_TAKES} newest takes. Export the keepers.`);
}

/** Drop a take from storage and free its cached audio. */
function forgetTake(t: Take): void {
  void removeTake(t.id);
  const url = wavUrls.get(t.id);
  if (url) URL.revokeObjectURL(url);
  wavUrls.delete(t.id);
  buffers.delete(t.id);
}

async function toggleRecord(): Promise<void> {
  if (capture.state === 'recording') {
    capture.stop();
    return;
  }
  if (capture.state !== 'idle' || finalizing) return;
  engine.resume();
  try {
    await engine.ready;
  } catch {
    toast('Recording needs a newer browser (AudioWorklet).');
    return;
  }
  stopPlayback();
  if (!capture.start()) return;
  if (view() === 'visual') {
    if (videoCapture.supported && videoCapture.start(visCanvas, engine.mediaStream())) {
      videoCapture.recording = true;
      $('#vis-badge').hidden = false;
    } else {
      toast('This browser can’t record video. Audio is still recording.');
    }
  }
}
recBtn.addEventListener('click', () => void toggleRecord());

// ------------------------------------------------------------------ playback

let playback: { id: string; src: AudioBufferSourceNode; start: number; offset: number; dur: number; raf: number } | null = null;
const buffers = new Map<string, AudioBuffer>();

function togglePlay(t: Take, at?: number): void {
  if (playback?.id === t.id && at === undefined) {
    stopPlayback();
    return;
  }
  stopPlayback();
  engine.resume();
  let buf = buffers.get(t.id);
  if (!buf) {
    buf = engine.ctx.createBuffer(2, t.audio.left.length, t.audio.sampleRate);
    buf.copyToChannel(t.audio.left, 0);
    buf.copyToChannel(t.audio.right, 1);
    buffers.set(t.id, buf);
  }
  const src = engine.ctx.createBufferSource();
  src.buffer = buf;
  // Straight to the speakers: playback shouldn't be re-recorded or re-effected.
  src.connect(engine.ctx.destination);
  const offset = Math.max(0, Math.min(0.999, at ?? 0)) * buf.duration;
  src.start(0, offset);
  const p = { id: t.id, src, start: engine.ctx.currentTime, offset, dur: buf.duration, raf: 0 };
  playback = p;
  src.onended = () => {
    if (playback === p) stopPlayback();
  };
  const tick = () => {
    if (playback !== p) return;
    const pos = (p.offset + engine.ctx.currentTime - p.start) / p.dur;
    takesView.setPlayback(p.id, Math.min(1, pos));
    p.raf = requestAnimationFrame(tick);
  };
  tick();
}

function stopPlayback(): void {
  if (!playback) return;
  const p = playback;
  playback = null;
  cancelAnimationFrame(p.raf);
  p.src.onended = null;
  try {
    p.src.stop();
  } catch {
    /* already ended */
  }
  takesView.setPlayback(null);
}

function playLatest(): void {
  if (takes[0]) togglePlay(takes[0]);
}

function deleteTake(t: Take): void {
  if (playback?.id === t.id) stopPlayback();
  const idx = takes.indexOf(t);
  if (idx < 0) return;
  takes.splice(idx, 1);
  takesView.remove(t.id);
  // Gone from storage right away, so a reload can't bring it back.
  // Undo writes it back.
  void removeTake(t.id);
  toast(`${t.name} deleted.`, {
    action: {
      label: 'undo',
      run: () => {
        takes.push(t);
        takes.sort((a, b) => b.createdAt - a.createdAt);
        takesView.add(t, false);
        reorderTakes();
        void saveTake(t);
        enforceTakeLimit();
      },
    },
    onExpire: () => forgetTake(t),
  });
}

function reorderTakes(): void {
  const list = $('#takes');
  for (const t of takes) {
    const li = list.querySelector(`[data-id="${t.id}"]`);
    if (li) list.append(li);
  }
}

// ------------------------------------------------------------------ dialogs

function openHelp(): void {
  const d = $<HTMLDialogElement>('#help-dialog');
  if (!d.open) {
    releaseAllNotes();
    d.showModal();
  }
}
$('#help-btn').addEventListener('click', openHelp);

function buildHelpKeys(): void {
  const host = $('#help-keys');
  const top = [['W', 'C#'], ['E', 'D#'], null, ['T', 'F#'], ['Y', 'G#'], ['U', 'A#']];
  const bottom = [['A', 'C'], ['S', 'D'], ['D', 'E'], ['F', 'F'], ['G', 'G'], ['H', 'A'], ['J', 'B'], ['K', 'C']];
  const row = (items: (string[] | null)[], cls: string) =>
    `<div class="help-keys__row ${cls}">${items
      .map((k) => (k ? `<span class="help-key"><kbd>${k[0]}</kbd><small>${k[1]}</small></span>` : '<span class="help-key help-key--gap"></span>'))
      .join('')}</div>`;
  host.innerHTML = row(top, 'help-keys__row--top') + row(bottom, '');
}

const feedbackDialog = $<HTMLDialogElement>('#feedback-dialog');
const feedbackForm = $<HTMLFormElement>('#feedback-form');
$('#feedback-btn').addEventListener('click', () => {
  releaseAllNotes();
  $('#feedback-note').textContent = '';
  feedbackDialog.showModal();
  feedbackForm.querySelector('textarea')!.focus();
});
$('#feedback-cancel').addEventListener('click', () => feedbackDialog.close());
for (const d of [feedbackDialog, $<HTMLDialogElement>('#help-dialog')]) {
  // Click on the backdrop closes.
  d.addEventListener('click', (e) => {
    if (e.target === d) d.close();
  });
}

feedbackForm.addEventListener('submit', async (e) => {
  e.preventDefault();
  const data = new FormData(feedbackForm);
  const message = String(data.get('message') ?? '').trim();
  if (!message) return;
  const send = $<HTMLButtonElement>('#feedback-send');
  const note = $('#feedback-note');
  send.disabled = true;
  note.textContent = 'sending…';
  try {
    const res = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        message,
        name: String(data.get('name') ?? '').trim(),
        website: String(data.get('website') ?? ''),
        context: { view: view(), preset: PRESETS[presetIdx].id, takes: takes.length, ua: navigator.userAgent },
      }),
    });
    if (!res.ok) throw new Error(String(res.status));
    note.textContent = 'Got it. Thank you!';
    feedbackForm.reset();
    setTimeout(() => feedbackDialog.close(), 1100);
  } catch {
    try {
      await navigator.clipboard.writeText(message);
      note.textContent = 'Could not send right now, so your note is copied to the clipboard.';
    } catch {
      note.textContent = 'Could not send right now. Please try again in a moment.';
    }
  } finally {
    send.disabled = false;
  }
});

// -------------------------------------------------------------------- theme

$('#theme-btn').addEventListener('click', () => {
  const root = document.documentElement;
  const dark = root.dataset.theme ? root.dataset.theme === 'dark' : matchMedia('(prefers-color-scheme: dark)').matches;
  root.dataset.theme = dark ? 'light' : 'dark';
  storeSet('theme', root.dataset.theme);
});

// ---------------------------------------------------------------- landing art

function drawLandingArt(): void {
  document.querySelectorAll<HTMLElement>('.choice__art').forEach((el) => {
    const seed = newSeed();
    const visual = el.dataset.art === 'visual';
    sketch(el, (rc, w, h) => {
      const o = ink({ seed, strokeWidth: 1.3 });
      const nodes: SVGElement[] = [];
      const kw = Math.min(w * 0.11, 26);
      const kx = w / 2 - kw * 3.5;
      const ky = h * 0.56;
      const kh = h * 0.36;
      for (let i = 0; i < 7; i++) nodes.push(rc.path(roundedRect(kx + i * kw, ky, kw, kh, 3), o));
      for (const i of [0, 1, 3, 4, 5]) {
        nodes.push(rc.rectangle(kx + (i + 1) * kw - kw * 0.3, ky, kw * 0.6, kh * 0.58, ink({ seed: seed + i, fill: 'currentColor', fillStyle: 'hachure', hachureGap: 2.4 })));
      }
      if (visual) {
        nodes.push(rc.circle(w / 2, h * 0.27, h * 0.36, ink({ seed: seed + 9 })));
        nodes.push(rc.circle(w / 2, h * 0.27, h * 0.22, ink({ seed: seed + 10, strokeWidth: 0.9 })));
      } else {
        const pts: [number, number][] = [];
        for (let i = 0; i <= 40; i++) {
          const x = w * 0.2 + (i / 40) * w * 0.6;
          const env = Math.sin((i / 40) * Math.PI);
          pts.push([x, h * 0.27 + Math.sin(i * 0.9) * h * 0.12 * env]);
        }
        nodes.push(rc.curve(pts, ink({ seed: seed + 11 })));
      }
      return nodes;
    });
  });
}

// -------------------------------------------------------------------- boot

function boot(): void {
  buildEffects();
  buildHelpKeys();
  drawLandingArt();
  sketchAll();
  renderPreset();
  renderStatus();
  renderVisControls();
  $('#oct-value').textContent = String(settings.octave);
  if (storeGet('played')) app.classList.add('has-played');

  // Synth presets work immediately; the piano needs its samples first.
  engine.setPreset(PRESETS[presetIdx]);
  engine
    .loadPiano((f) => {
      if (PRESETS[presetIdx].kind === 'sampler' && f < 1) flashStatus(`loading piano ${Math.round(f * 100)}%`, 4000);
    })
    .then(() => {
      pianoReady = true;
      if (PRESETS[presetIdx].kind === 'sampler') {
        engine.setPreset(PRESETS[presetIdx]);
        renderStatus();
      }
    })
    .catch(() => {
      toast('The piano samples did not load. Synth sounds still work.');
      if (PRESETS[presetIdx].kind === 'sampler') setPreset(presetIndex('electric-piano'));
    });
  engine.ready.catch(() => toast('Effects need a newer browser. Plain sounds still work.'));

  const hash = location.hash.slice(1);
  go(hash === 'audio' || hash === 'visual' ? hash : 'landing', false);
  window.addEventListener('hashchange', () => {
    const h = location.hash.slice(1);
    go(h === 'audio' || h === 'visual' ? h : 'landing');
  });

  void loadTakes().then((saved) => {
    for (const t of saved.slice(0, MAX_TAKES)) {
      takes.push(t);
      takesView.add(t, false);
    }
    for (const extra of saved.slice(MAX_TAKES)) void removeTake(extra.id);
  });

  runScope();
  // Canvas text needs the font loaded before the first caption is drawn.
  void document.fonts.load('600 48px "Shantell Sans Variable"');

  if (import.meta.env.PROD) registerSW({ immediate: true });
  let deferred: (Event & { prompt(): Promise<void> }) | null = null;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as typeof deferred;
    $('#install-btn').hidden = false;
  });
  $('#install-btn').addEventListener('click', () => {
    void deferred?.prompt();
    deferred = null;
    $('#install-btn').hidden = true;
  });

  // Exposed for automated tests and the curious.
  (window as unknown as Record<string, unknown>).spiel = { engine, settings, takes, noteName };
}

boot();
